/* eslint-disable max-classes-per-file */
import type { State as PageInfoState } from "../../reducers/pageInfo";
import type { State as PageVersionsState, Version } from "../../reducers/pageVersions";
import type { Resource } from "../../reducers/pageResources";
import type {
  PageDocumnetModifierType,
  PageResourceType,
  PageToSaveType,
} from "../../containers/FloatingControlButtons/types/FloatingControlButtons.types";
import type { Modifier } from "../../reducers/pageModifiers/reducer";

export default class PageType {
  pageTypeTitle: string = "";

  pageTypeKey: string = "";

  page: PageToSaveType | undefined;

  version: Version | undefined;

  modifiers: Array<PageDocumnetModifierType> = [];

  resources: Array<PageResourceType> = [];

  pageInfo: PageInfoState | undefined;

  pageVersions: PageVersionsState | undefined;

  pageModifiers: Array<Modifier> = [];

  pageResources: Array<Resource> = [];

  preparePageData(
    pageInfo: PageInfoState,
    pageVersions: PageVersionsState,
    pageModifiers: Array<Modifier>,
    pageResources: Array<Resource>
  ): void {
    this.pageInfo = pageInfo;
    this.pageVersions = pageVersions;
    this.pageModifiers = pageModifiers;
    this.pageResources = pageResources;

    const {
      versionType,
    }: PageInfoState = this.pageInfo;

    const versionData = this.pageVersions.get(versionType);
    if (!versionData) {
      return;
    }
    const {
      html: content,
      css,
      js,
    }: Version = versionData as unknown as Version;

    this.page = {
      name: this.pageInfo.get("name"),
      type: this.pageInfo.get("pageType"),
      tags: this.pageInfo.get("tags"),
      contentType: "text/html",
      aliases: [this.pageInfo.get("url")],
      description: "",
      languages: ["en"],
      documentModifiers: [],
      content,
      css,
      js,
    };
  }

  _proceedPageModifiersData() {
    this.modifiers = this.pageModifiers
      .reduce((
        result: Array<PageDocumnetModifierType>,
        modifier: Modifier
      ): Array<PageDocumnetModifierType> => result.concat({
        anchor: modifier.name,
        name: modifier.name,
        type: modifier.type,
        content: modifier.content,
        description: modifier.description,
      }), []);
  }

  _proceedPageResourcesData() {
    const resources: Array<Resource> = this.pageResources;
    this.resources = resources.reduce((
      result: Array<PageResourceType>,
      resource: Resource
    ): Array<PageResourceType> => {
      if (resource.resourceId) {
        return result.concat({ resourceId: resource.resourceId, name: resource.name });
      }
      if (resource.base64URL) {
        return result.concat({ resource: resource.base64URL, name: resource.name });
      }
      return result;
    }, []);
  }

  getPageData() {
    return this.page as PageToSaveType;
  }
}

export class Page extends PageType {
  pageTypeTitle = "Page";

  pageTypeKey = "page";

  preparePageData(
    pageInfo: PageInfoState,
    pageVersions: PageVersionsState,
    pageModifiers: Array<Modifier>,
    pageResources: Array<Resource>
  ): void {
    super.preparePageData(pageInfo, pageVersions, pageModifiers, pageResources);
    this._proceedPageResourcesData();

    if (this.page) {
      this.page = {
        ...this.page,
        resources: this.resources,
      };
    }
  }
}

export class Pft extends PageType {
  pageTypeTitle = "Pft";

  pageTypeKey = "pft";

  preparePageData(
    pageInfo: PageInfoState,
    pageVersions: PageVersionsState,
    pageModifiers: Array<Modifier>,
    pageResources: Array<Resource>
  ): void {
    super.preparePageData(pageInfo, pageVersions, pageModifiers, pageResources);
    this._proceedPageModifiersData();
    this._proceedPageResourcesData();

    if (!this.pageInfo || !this.page) {
      return;
    }

    const template = this.pageInfo.get("template");
    const { templateId } = template;

    this.page = {
      ...this.page,
      parentId: templateId,
      documentModifiers: this.modifiers,
      resources: this.resources,
    };
  }

  _proceedPageModifiersData() {
    this.modifiers = this.pageModifiers
      .filter(({ content }) => Boolean(content))
      .reduce((
        result: Array<PageDocumnetModifierType>,
        modifier: Modifier
      ): Array<PageDocumnetModifierType> => result.concat({
        anchor: modifier.name,
        name: modifier.name,
        type: modifier.type,
        content: modifier.content,
        description: modifier.description,
      }), []);
  }
}
export class Template extends PageType {
  pageTypeTitle = "Template";

  pageTypeKey = "template";

  preparePageData(
    pageInfo: PageInfoState,
    pageVersions: PageVersionsState,
    pageModifiers: Array<Modifier>,
    pageResources: Array<Resource>
  ): void {
    super.preparePageData(pageInfo, pageVersions, pageModifiers, pageResources);
    this._proceedPageModifiersData();
    this._proceedPageResourcesData();

    if (this.page) {
      this.page = {
        ...this.page,
        documentModifiers: this.modifiers,
        resources: this.resources,
      };
    }
  }
}

export class Custom extends PageType {
  pageTypeTitle = "Custom";

  pageTypeKey = "custom";

  preparePageData(
    pageInfo: PageInfoState,
    pageVersions: PageVersionsState,
    pageModifiers: Array<Modifier>,
    pageResources: Array<Resource>
  ): void {
    super.preparePageData(pageInfo, pageVersions, pageModifiers, pageResources);
    this._proceedPageResourcesData();

    if (this.page) {
      this.page = {
        ...this.page,
        resources: this.resources,
      };
    }
  }
}
